import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { makeMaybeFetchTokenQuotes } from '../api/coingecko';
import { renderNumber } from '../utils/helpers';
import { makeKey } from "../utils/helpers";

import { getDataForChain } from '../data/getData';
import { PROJECT_SUBMISSION_FORM_URL } from '../data/constants';
import { CHAIN_DATA, CHAINS, DEFAULT_CHAIN } from '../data/metaData';

import VerifiedIcon from './VerifiedIcon';

const maybeFetchTokenQuotes = makeMaybeFetchTokenQuotes();

const Home = () => {
	const { chain: selectedChain } = useParams();
	const [tokenDataByChain, setTokenDataByChain] = useState({});

	const {
		featuredProjects,
		trendingProjects,
		processedData,
	} = getDataForChain(selectedChain);
	const tokenId = CHAINS.includes(selectedChain)
		? CHAIN_DATA[selectedChain].coingeckoTokenId
		: '';
	
	useEffect(() => {
		const fetchData = async (tokenId) => {
			const data = await maybeFetchTokenQuotes(tokenId);

			setTokenDataByChain({
				...tokenDataByChain,
				[tokenId]: data,
			});
		};

		if (tokenId && !tokenDataByChain[tokenId]) {
			fetchData(tokenId);
		}
	}, [tokenId, tokenDataByChain]);

	const {
		coingecko_rank,
		market_cap,
		market_cap_rank,
		price,
		price_change_24h,
		price_change_percentage_24h,
		tokenID,
		total_volume,
	} = tokenDataByChain[tokenId] || {};
	const { name: chainName } = CHAIN_DATA[selectedChain] || CHAIN_DATA[DEFAULT_CHAIN];
	

	const hasData = !!price;
	const isPriceChangePositive = (price_change_24h || 0) >= 0;

	const renderFeaturedProjects = () => (
		<div className="columns">
			{featuredProjects
				.map(({ name, description, image, isVerified = false }) => (
					<div key={name} className="column col-3 col-lg-6 col-sm-12">
						<Link className="article-link" title={description} to={`/${selectedChain}/detail/${makeKey(name)}`} rel='noreferrer'>
							<div className="article-image">
								<img src={image} className="img-responsive" alt={description} />
								{isVerified && <VerifiedIcon />}
							</div>
							<div className="article-content">{description}</div>
						</Link>
					</div>
				))}
		</div>
	);

	const renderTrendingProjects = () => (
		<div className="awesome-trending-content">
			{trendingProjects
				.map(({ name, image, isVerified = false }) => (
					<Link key={name} className="near-item-sm" title={name} to={`/${selectedChain}/detail/${makeKey(name)}`} rel="noreferrer">
						<div className="tile-icon">
							<img src={image} alt={name} />
							{isVerified && <VerifiedIcon />}
						</div>
						<div className="tile-content"><h2 className="tile-title">{name}</h2></div>
					</Link>
				))}
		</div>
	);

	return (
		<div className='home-page'>
			<div className='section-main'>
				<div className='container grid-xl'>
					<div className="awesome-home text-center">
						<h1 className="hero-title text-bold">
							{`The curated list of ${processedData.total} projects building on the ${chainName} ecosystem.`}
						</h1>
						<div className="hero-actions">
							<Link className="action-link" to={`/${selectedChain}/projects`} rel="noopener noreferrer" title="Explore all projects">
								<div className="action-icon">❖</div>Explore all
							</Link>
							<a className="action-link" href={PROJECT_SUBMISSION_FORM_URL} target="_blank" rel="noopener noreferrer" title="Submit a project"><div className="action-icon">+</div>Submit a project</a>
						</div>
					</div>
				</div>
				<div className="container awesome-articles grid-xl">
					{renderFeaturedProjects()}
				</div>
				<div className="container awesome-stats">
					<a className="stats-link" target="_blank" href={`https://www.coingecko.com/en/coins/${tokenID}`} rel="noreferrer">
						<div className="stats-value">{hasData ? `$${renderNumber(price)}` : '--'}</div>
						<div className="stats-info">
							<div className="stats-info-label text-bold">{selectedChain} Price</div>
							<div className="stats-info-label">24H: <span className={`stats-info-value ${isPriceChangePositive ? 'text-success' : 'text-error'}`}>
								{hasData ? `${isPriceChangePositive ? '+' : ''}${price_change_percentage_24h}%` : '--'}
							</span></div>
						</div>
					</a>
					<a className="stats-link" target="_blank" href={`https://www.coingecko.com/en/coins/${tokenID}`} rel="noreferrer">
						<div className="stats-value">{hasData ? `$${renderNumber(market_cap)}` : '--'}</div>
						<div className="stats-info">
							<div className="stats-info-label text-bold">{selectedChain} MarketCap</div>
							<div className="stats-info-label">{`Rank: #${market_cap_rank ? market_cap_rank : '--'}`}</div>
						</div>
					</a>
					<a className="stats-link" target="_blank" href={`https://www.coingecko.com/en/coins/${tokenID}`} rel="noreferrer">
						<div className="stats-value">{hasData ? renderNumber(coingecko_rank) : '--'}</div>
						<div className="stats-info">
							<div className="stats-info-label text-bold">Coingecko Rank</div>
							<div className="stats-info-label">--</div>
						</div>
					</a>
					<a className="stats-link" target="_blank" href={`https://www.coingecko.com/en/coins/${tokenID}`} rel="noreferrer">
						<div className="stats-value">{hasData ? `$${renderNumber(total_volume)}` : '--'}</div>
						<div className="stats-info">
							<div className="stats-info-label text-bold">Trading Volume</div>
							<div className="stats-info-label">--</div>
						</div>
					</a>
				</div>
			</div>
			<div className='section-main'>
				<div className="container awesome-trending grid-xl" id="trending">
					<h2 className="awesome-trending-header text-bold">Trending Projects</h2>
					{renderTrendingProjects()}
				</div>
			</div>
		</div>
	);
}

export default Home;
